import { useState } from 'react'
import {
  LBPopover,
  LBPopoverTrigger,
  LBPopoverContent,
  LBCalendar,
  LBButton,
  LBSeparator,
  LBCalendarIcon
} from '@linearb/buzz'
import type { LBDateRange } from '@linearb/buzz'
import dayjs from 'dayjs'
import { getDateRangePresets } from './dateRangePresets'
import type { DateRangePreset } from './dateRangePresets'

interface Props {
  selected: LBDateRange | undefined
  onChange: (range: LBDateRange | undefined) => void
  placeholder?: string
  // Override the preset sidebar (e.g. backfill uses past-only, <=90d presets).
  presets?: DateRangePreset[]
}

const formatTriggerLabel = (
  range: LBDateRange | undefined,
  placeholder?: string,
  presetLabel?: string
): string => {
  const from = range?.from
  const to = range?.to
  if (from && to) {
    const dates = `${dayjs(from).format('MMM DD, YYYY')} - ${dayjs(to).format('MMM DD, YYYY')}`
    return presetLabel ? `${presetLabel} · ${dates}` : dates
  }
  if (from) {
    return dayjs(from).format('MMM DD, YYYY')
  }
  return placeholder ?? 'Pick a date range'
}

// Date range picker with a preset sidebar (Current month, Past 30 days, Q1…).
// Ported from quality-web-app's DateRangePickerWithPresets to dayjs.
const DateRangePickerWithPresets: React.FC<Props> = ({
  selected,
  onChange,
  placeholder,
  presets: presetsProp
}) => {
  const [open, setOpen] = useState(false)
  const [clickedLabel, setClickedLabel] = useState<string>()
  const presets = presetsProp ?? getDateRangePresets()
  // Matched by dates, so a range picked by hand on the calendar lights up its preset
  // too; when presets overlap (e.g. Current month vs Past 30 days) the clicked one wins.
  const matching =
    selected?.from && selected?.to
      ? presets.filter(p => {
        const { from, to } = p.getRange()
        return (
          dayjs(selected.from).isSame(from, 'day') &&
          dayjs(selected.to).isSame(to, 'day')
        )
      })
      : []
  const activePreset =
    matching.find(p => p.label === clickedLabel) ?? matching[0]

  return (
    <LBPopover open={open} onOpenChange={setOpen}>
      <LBPopoverTrigger asChild>
        <LBButton
          variant='outline'
          className='justify-start text-left font-normal rounded-md max-w-full'
        >
          <LBCalendarIcon />
          <span className='truncate'>
            {formatTriggerLabel(selected, placeholder, activePreset?.label)}
          </span>
        </LBButton>
      </LBPopoverTrigger>
      <LBPopoverContent
        className='w-auto max-w-[calc(100vw-1rem)] max-h-[85vh] overflow-y-auto p-0'
        align='start'
      >
        <div className='flex flex-col sm:flex-row'>
          <div className='flex flex-wrap gap-1 p-3 sm:flex-col sm:min-w-[150px]'>
            {presets.map(preset => (
              <LBButton
                key={preset.label}
                variant='ghost'
                className={`justify-start font-normal ${preset === activePreset ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 font-medium' : ''}`}
                aria-pressed={preset === activePreset}
                onClick={() => {
                  setClickedLabel(preset.label)
                  onChange(preset.getRange())
                  setOpen(false)
                }}
              >
                {preset.label}
              </LBButton>
            ))}
          </div>
          <LBSeparator className='sm:hidden' />
          <LBSeparator
            orientation='vertical'
            className='hidden h-auto sm:block'
          />
          <div className='flex flex-col p-3'>
            <LBCalendar
              mode='range'
              autoFocus
              numberOfMonths={2}
              // buzz's calendar shows outside days by default, which highlights
              // the range endpoint twice (once as an adjacent-month "outside"
              // day). Turn them off so each date is highlighted exactly once.
              showOutsideDays={false}
              defaultMonth={selected?.from}
              selected={selected}
              onSelect={(range, selectedDay) =>
                // react-day-picker keeps `from` sticky and only moves `to` once
                // a full range exists, which feels broken. When a complete range
                // is already selected, treat the next click as the start of a
                // fresh range; otherwise take the range it built.
                onChange(
                  selected?.from && selected?.to
                    ? { from: selectedDay, to: undefined }
                    : { from: range?.from, to: range?.to }
                )
              }
            />
            <div className='flex justify-end pt-2'>
              <LBButton onClick={() => setOpen(false)}>Apply</LBButton>
            </div>
          </div>
        </div>
      </LBPopoverContent>
    </LBPopover>
  )
}

export default DateRangePickerWithPresets
